import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  DropdownTrigger,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Tooltip,
  useDisclosure,
  Select,
  SelectItem,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import SelectComponent from "../common/form/SelectComponent";
import AddProductModal from "../common/modal/AddProductModal";
import InternalDebugModal from "../common/modal/InternalDebugModal";
import ValidateAllProductsModal from "../common/modal/ValidateAllProductsModal";
import useUserProfile from "../../hooks/useUserProfile";
import ProductQuantity from "./ProductQuantity";
import ProductDiscount from "./ProductDiscount";
import ProductVerificationTooltip from "./ProductVerificationTooltip";
import CheckboxActive from "../common/form/CheckboxActive";
import { useUseCases } from "@/context/UseCasesProvider";

import { PiWarningFill, PiWarningDiamondFill, PiCheckSquareFill, PiTableFill, PiCardsFill } from "react-icons/pi";
import { GiSandsOfTime } from "react-icons/gi";
import { CgUnavailable } from "react-icons/cg";
import { VerticalDotsIcon } from "../orderList/VerticalDotsIcon";
import { IoIosCheckmarkCircle, IoMdAddCircleOutline } from "react-icons/io";
import { RiRepeat2Line } from "react-icons/ri";

export default function Products({
  control,
  setValue,
  data,
  selectedCustomer,
  onProductDelete,
  onAddProduct,
  onProductUpdate,
  currentPage,
  shouldReloadData,
  setShouldReloadData,
  onHoveredShapeId,
  hoveredShapeDraw,
  currentErrors,
  trackingError,
  selectedIndices,
  onSelectedIndicesChange,
  openAddProduct,
  onModalAddProduct,
}) {
  const [localData, setLocalData] = useState(data);
  const { t } = useTranslation();
  const [localSelectedIndices, setLocalSelectedIndices] = useState(new Set());
  const {
    isOpen: isAddProductModalOpen,
    onOpen: onAddProductModalOpen,
    onClose: onAddProductModalClose,
  } = useDisclosure();
  const {
    isOpen: isInternalDebugModalOpen,
    onOpen: onInternalDebugModalOpen,
    onClose: onInternalDebugModalClose,
  } = useDisclosure();
  const {
    isOpen: isValidateAllModalOpen,
    onOpen: onValidateAllModalOpen,
    onClose: onValidateAllModalClose,
  } = useDisclosure();
  const [productsToValidateCount, setProductsToValidateCount] = useState(0);
  const { isDataReadOnlyOperators, isAdminVistaflow } = useUserProfile();
  const settingsInterface = useUseCases();

  useEffect(() => {
    if (shouldReloadData) {
      setLocalData(data);
      setLocalSelectedIndices(new Set());
      setShouldReloadData(false);
    } else {
      setLocalSelectedIndices(selectedIndices);
    }
  }, [shouldReloadData, data, selectedIndices, setShouldReloadData]);

  const isProductVariantVisible = settingsInterface?.settings?.display?.product_display_column?.product_variant_name;
  const isProductDiscountVisible = settingsInterface?.settings?.display?.product_display_column?.product_discount;
  
  const INITIAL_VISIBLE_COLUMNS = [
    // "productName",
    "status",
    "productEdit",
    ...(isProductVariantVisible ? ["productVariant"] : []),
    ...(isProductDiscountVisible ? ["productDiscount"] : []),
    "productQuantity",
    "actions",
  ];

  const columns = useMemo(
    () => [
      // { name: "Produit trouvé", uid: "productName", sortable: true },
      {
        name: t("status"),
        uid: "status",
        sortable: true,
        style: { maxWidth: "40px" },
      },
      {
        name: t("article"),
        uid: "productEdit",
        sortable: true,
        style: { maxWidth: "3000px" },
      },
      {
        name: t("variant"),
        uid: "productVariant",
        sortable: true,
        style: { maxWidth: "110px" },
      },
      {
        name: t("discount"),
        uid: "productDiscount",
        sortable: true,
        style: { maxWidth: "70px" },
      },
      {
        name: t("quantity"),
        uid: "productQuantity",
        sortable: true,
        style: { minWidth: "100px" },
      },
      { name: t("actions"), uid: "actions", style: { maxWidth: "90px" } },
    ],
    [t],
  );

  const [items, setItems] = useState([]);
  const [temporaryProduct, setTemporaryProduct] = useState(null);
  const { getValues, unregister } = useFormContext();
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("productsViewMode");
    return saved || "table";
  });

  useEffect(() => {
    localStorage.setItem("productsViewMode", viewMode);
  }, [viewMode]);

  const [visibleColumns] = React.useState(new Set(INITIAL_VISIBLE_COLUMNS));

  const [selectedKeys, setSelectedKeys] = React.useState(new Set([]));
  
  const [isProductQuantityMode, setIsProductQuantityMode] = useState(settingsInterface?.settings?.display?.product_quantity?.unit_by_default ?? "moq");
  
  const allQuantityModes = [
    {key: "atom", label: settingsInterface?.settings?.display?.product_quantity?.label_for_atom ?? "unit"},
    {key: "moq", label: settingsInterface?.settings?.display?.product_quantity?.label_for_moq ?? "moq"},
    {key: "delivery", label: settingsInterface?.settings?.display?.product_quantity?.label_for_delivery ?? "delivery"}
  ];
  
  const availableQuantityModes = useMemo(() => {
    return allQuantityModes.filter(mode => {
      return settingsInterface?.settings?.interface?.product_quantity?.[mode.key] === true;
    });
  }, [settingsInterface]);

  const selectWidth = useMemo(() => {
    const maxLabelLength = Math.max(...availableQuantityModes.map(mode => mode.label.length));
    if (maxLabelLength <= 8) return "w-28";
    if (maxLabelLength <= 12) return "w-32";
    if (maxLabelLength <= 16) return "w-40";
    return "w-40";
  }, [availableQuantityModes]);

  const headerColumns = React.useMemo(() => {
    if (visibleColumns === "all") return columns;

    return columns.filter((column) =>
      Array.from(visibleColumns).includes(column.uid),
    );
  }, [visibleColumns, columns]);

  const { data: productsData } = useQuery({
    queryKey: ["allProducts"],
    enabled: false,
  });
  const checkProductVariants = useCallback(
    (productId) => {
      const hasVariants = productsData?.some(
        (product) =>
          product.product_id === productId && product.product_variant_name,
      );

      return hasVariants ? 100 : 0;
    },
    [productsData],
  );

  const checkProductAvailability = useCallback(
    (productId) => {
      const productAvailability = productsData?.some(
        (product) =>
          product.product_id === productId && product.product_available,
      );

      return Boolean(productAvailability);
    },
    [productsData],
  );

  const checkProductSwitchAvailable = useCallback(
    (productId) => {
      const product = productsData?.find((p) => p.product_id === productId);
      const description = product?.product_availability_description?.toLowerCase() || "";
      return (
        product?.product_available === false &&
        (description.includes("switch possible") || description.includes("référence du lot"))
      );
    },
    [productsData],
  );

  const checkProductExistsInApi = useCallback(
    (productId) => {
      const product = productsData?.find((p) => p.product_id === productId);
      return !!product?.product_name;
    },
    [productsData],
  );

  useEffect(() => {
    if (!localData?.order?.products) return;

    const productsWithIndex = localData.order.products.map((product, index) => ({
      ...product,
      index,
    }));

    // Filtrer les produits pour la page courante
    let allItems = productsWithIndex.filter(
      (product) => product.product_pageNumber.value === currentPage
    );

    // Inclure le temporaryProduct si présent et correspond à la page courante
    if (temporaryProduct && temporaryProduct.product_pageNumber.value === currentPage) {
      allItems = [...allItems, temporaryProduct];
    }

    // Utiliser allItems pour créer productDetails
    const productDetails = allItems.map((product) => {
      const errorInfo = currentErrors.find(
        (te) => te.index === product.index,
      );
      const productId = product.product_id?.value || "";

      return {
        productName: product.product_name?.valueBulk || "",
        productID: productId,
        productStatus: product.product_id?.valueConfidence || 0,
        productVariant: product.product_variant_id?.value || "",
        productVariantStatus: product.product_variant_id?.valueConfidence || 0,
        productQuantity: product.product_quantity?.value || "",
        productQuantityStatus: product.product_quantity?.valueConfidence || 0,
        productPacking: product.product_packing?.value || "",
        productPackingCustomer: product.product_packing_customer?.value || "",
        productPackingCustomerStatus: product.product_packing_customer?.valueConfidence ?? 0,
        productConfidenceSource: product.product_id?.valueConfidenceSource || "",
        productIdCorrected: errorInfo
          ? errorInfo.productIdCorrected
          : false,
        quantityIdCorrected: errorInfo
          ? errorInfo.quantityIdCorrected
          : false,
        variantIdCorrected: errorInfo
          ? errorInfo.variantIdCorrected
          : false,
        packingCustomerIdCorrected: errorInfo
          ? errorInfo.packingCustomerIdCorrected
          : true,
        productForceVerification: productsData?.find((p) => p.product_id === productId)?.force_verification === true,
        productProposals: product.product_id?.proposals || [],
        index: product.index,
      };
    });

    setItems(productDetails);
  }, [localData, temporaryProduct, currentPage, trackingError, currentErrors, productsData]);

  const handleProductSelectionChange = useCallback(
    (index, newValue) => {
      if (temporaryProduct && index === temporaryProduct.index) {
        const selectedProductOptions = {
          position: temporaryProduct.position,
          product: newValue,
          quantity: temporaryProduct.quantity,
          variant: temporaryProduct.variant,
        };
  
        handleAddProduct(selectedProductOptions, true);
  
        setTemporaryProduct(null);
      } else {
        // Update selection status if necessary
        onSelectedIndicesChange(index);

        // check if variant to define valueconfidence
        const hasVariants = checkProductVariants(newValue.value);

        setValue(`products-${index}.product_id`, newValue.value);
        setValue(`products-${index}.product_name`, newValue.label);

        // Update localData with new product data
        setLocalData((prevLocalData) => {
          const updatedProducts = [...prevLocalData.order.products];

          if (updatedProducts[index] && updatedProducts[index].product_name) {
            updatedProducts[index].product_name.value = newValue.label;
            updatedProducts[index].product_id.value = newValue.value;

            const confidenceSource =
              updatedProducts[index].product_id.valueConfidenceSource === "hitl"
                ? "hitl"
                : "update";

            updatedProducts[index].product_id.valueConfidenceSource = confidenceSource;
            updatedProducts[index].product_name.valueConfidenceSource = confidenceSource;

            if (!hasVariants) {
              updatedProducts[index].product_variant_id.value = null;
              updatedProducts[index].product_variant_id.valueConfidence = 100;
              updatedProducts[index].product_variant_id.valueConfidenceSource = "update";
            }

            if (
              hasVariants &&
              updatedProducts[index] &&
              updatedProducts[index].product_variant_name &&
              updatedProducts[index].product_variant_name.value != null
            ) {
              trackingError(index, { productError: true, variantError: true });
            } else {
              trackingError(index, { productError: true });
            }
          }
          return {
            ...prevLocalData,
            order: { ...prevLocalData.order, products: updatedProducts },
          };
        });

        // Sync product selection to parent dataForm
        onProductUpdate?.(index, {
          product_id: { value: newValue.value },
          product_name: { value: newValue.label },
        });
      }
    },
    [onSelectedIndicesChange, setLocalData, trackingError, checkProductVariants, setValue, onProductUpdate]
  );

  const handleVariantSelectionChange = useCallback(
    (index, newValue) => {
      // Update selection status if necessary
      onSelectedIndicesChange(index);

      // Update localData with new product data
      setLocalData((prevLocalData) => {
        const updatedProducts = [...prevLocalData.order.products];

        if (
          updatedProducts[index] &&
          updatedProducts[index].product_variant_id
        ) {
          updatedProducts[index].product_variant_id.value = newValue.value;
          updatedProducts[index].product_variant_id.valueConfidence = "100";
          updatedProducts[index].product_variant_id.valueConfidenceSource =
            "update";
          updatedProducts[index].product_variant_name.value = newValue.label;
          updatedProducts[index].product_variant_name.valueConfidence = "100";
          updatedProducts[index].product_variant_name.valueConfidenceSource =
            "update";
        }
        return {
          ...prevLocalData,
          order: { ...prevLocalData.order, products: updatedProducts },
        };
      });

      trackingError(index, { variantError: true });
    },
    [onSelectedIndicesChange, setLocalData, trackingError],
  );

  const getDefaultPackingCustomer = useCallback((productId) => {
    const productData = productsData?.find((item) => item.product_id === productId);
    if (!productData) return null;

    const productUnits = {
      moq: productData.product_moq_unit || "MOQ",
      atom: productData.product_atom_unit || "UNITE",
      delivery: productData.product_delivery_unit || "",
      production: productData.product_production_unit || "",
      piece: productData.product_piece_unit || undefined,
      base: productData.product_base_unit || undefined,
    };

    const productQuantities = {
      production: productData.product_production_quantity ? Number.parseFloat(productData.product_production_quantity) : undefined,
      piece: productData.product_piece_quantity ? Number.parseFloat(productData.product_piece_quantity) : undefined,
      base: productData.product_base_quantity ? Number.parseFloat(productData.product_base_quantity) : undefined,
    };

    const availableUnits = [];
    const addedLabels = new Set();

    if (productUnits.moq) {
      availableUnits.push({ key: "moq", label: productUnits.moq });
      addedLabels.add(productUnits.moq.toLowerCase());
    }

    if (productUnits.atom && !addedLabels.has(productUnits.atom.toLowerCase())) {
      availableUnits.push({ key: "atom", label: productUnits.atom });
      addedLabels.add(productUnits.atom.toLowerCase());
    }

    if (productUnits.delivery && !addedLabels.has(productUnits.delivery.toLowerCase())) {
      availableUnits.push({ key: "delivery", label: productUnits.delivery });
      addedLabels.add(productUnits.delivery.toLowerCase());
    }

    if (productUnits.production && productQuantities.production && !addedLabels.has(productUnits.production.toLowerCase())) {
      availableUnits.push({ key: "production", label: productUnits.production });
      addedLabels.add(productUnits.production.toLowerCase());
    }

    if (productUnits.piece && productQuantities.piece && !addedLabels.has(productUnits.piece.toLowerCase())) {
      availableUnits.push({ key: "piece", label: productUnits.piece });
      addedLabels.add(productUnits.piece.toLowerCase());
    }

    if (productUnits.base && productQuantities.base && !addedLabels.has(productUnits.base.toLowerCase())) {
      availableUnits.push({ key: "base", label: productUnits.base });
      addedLabels.add(productUnits.base.toLowerCase());
    }

    return availableUnits[0]?.label || productUnits.moq;
  }, [productsData]);

  const validateSelection = useCallback(
    (productId, productName, index, productQuantity) => {
      const newValue = {
        value: productId,
        label: `${productId} - ${productName}`,
      };
      const currentQuantity =
        typeof getValues(`productQuantity-${index}`) !== "undefined"
          ? getValues(`productQuantity-${index}`)
          : productQuantity;

      const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
      let currentPackingCustomer = getValues(`productPackingCustomer-${index}`);

      if (isTemplate2 && (!currentPackingCustomer || currentPackingCustomer === "")) {
        currentPackingCustomer = getDefaultPackingCustomer(productId);
        if (currentPackingCustomer) {
          setValue(`productPackingCustomer-${index}`, currentPackingCustomer);
        }
      }

      // React Hook Form update
      setValue(`products-${index}.product_id`, newValue.value);
      setValue(`products-${index}.product_name`, newValue.label);
      setValue(`productQuantity-${index}`, currentQuantity);

      if (isTemplate2 && currentPackingCustomer) {
        setValue(`products-${index}.product_packing_customer`, currentPackingCustomer);
      }

      setLocalData((prevLocalData) => {
        const updatedProducts = [...prevLocalData.order.products];
        if (updatedProducts[index]?.product_id) {
          updatedProducts[index].product_id.valueConfidenceSource =
            "hitl";
        }
        if (updatedProducts[index] && updatedProducts[index].product_quantity) {
          updatedProducts[index].product_quantity.value = currentQuantity;
          updatedProducts[index].product_quantity.valueConfidence = "100";
          updatedProducts[index].product_quantity.valueConfidenceSource =
            "hitl";
        }
        if (updatedProducts[index]?.product_variant_id?.value) {
          updatedProducts[index].product_variant_id.valueConfidence = 100;
          updatedProducts[index].product_variant_id.valueConfidenceSource = "hitl";
        }
        if (updatedProducts[index]?.product_variant_name?.value) {
          updatedProducts[index].product_variant_name.valueConfidence = 100;
          updatedProducts[index].product_variant_name.valueConfidenceSource = "hitl";
        }
        if (isTemplate2 && updatedProducts[index].product_packing_customer) {
          if (currentPackingCustomer) {
            updatedProducts[index].product_packing_customer.value = currentPackingCustomer;
          }
          updatedProducts[index].product_packing_customer.valueConfidence = 100;
          updatedProducts[index].product_packing_customer.valueConfidenceSource = "hitl";
        }
        return {
          ...prevLocalData,
          order: { ...prevLocalData.order, products: updatedProducts },
        };
      });

      if (onProductUpdate) {
        const currentVariant = getValues(`productVariants-${index}`);
        if (currentVariant) {
          onProductUpdate(index, {
            product_variant_id: { valueConfidence: 100, valueConfidenceSource: "hitl" },
            product_variant_name: { valueConfidence: 100, valueConfidenceSource: "hitl" },
          });
        }
      }

      // Local status updated to reflect validated selection
      handleProductSelectionChange(index, newValue);

      trackingError(index, {
        productError: true,
        quantityError: true,
        variantError: true,
        packingCustomerError: isTemplate2 ? true : undefined,
      });
    },
    [
      getValues,
      setValue,
      setLocalData,
      handleProductSelectionChange,
      trackingError,
      settingsInterface,
      getDefaultPackingCustomer,
      onProductUpdate,
    ],
  );

  const deleteSelection = useCallback(
    (index) => {
      // Delete local state product

      if (temporaryProduct && index === temporaryProduct.index) {
        setTemporaryProduct(null);
      } else {
        const totalCount = localData.order.products.length;

        // Shift all form values down by 1 for every product after the deleted one
        for (let i = index + 1; i < totalCount; i++) {
          const newIdx = i - 1;
          setValue(`products-${newIdx}`, getValues(`products-${i}`));
          setValue(`productQuantity-${newIdx}`, getValues(`productQuantity-${i}`));
          setValue(`productVariants-${newIdx}`, getValues(`productVariants-${i}`));
          setValue(`productPackingCustomer-${newIdx}`, getValues(`productPackingCustomer-${i}`));
        }

        const lastIdx = totalCount - 1;
        unregister(`products-${lastIdx}`);
        unregister(`productQuantity-${lastIdx}`);
        unregister(`productVariants-${lastIdx}`);
        unregister(`productPackingCustomer-${lastIdx}`);

        setItems((prevItems) => prevItems.filter((_, idx) => idx !== index));

        setLocalData((prevLocalData) => ({
          ...prevLocalData,
          order: {
            ...prevLocalData.order,
            products: prevLocalData.order.products.filter((_, idx) => idx !== index),
          },
        }));

        trackingError(index, { productError: true, quantityError: true });

        onProductDelete(index);
      }
    },
    [setItems, setLocalData, trackingError, onProductDelete, unregister, getValues, setValue, temporaryProduct, localData],
  );

  const handleAddProduct = useCallback(
    (selectedProductOptions, isQuickAdd = false) => {
      const { position, product, quantity, variant } = selectedProductOptions;
      const valueConfidence = isQuickAdd ? 0 : 100;
      const newProduct = {
        product_name: {
          value: product.label,
          valueAlgo: product.label,
          valueBulk: product.label,
        },
        product_id: {
          value: product.value,
          valueConfidence: valueConfidence,
          valueConfidenceSource: "manual",
        },
        product_variant_id: {
          value: variant.value,
          valueConfidence: 100,
        },
        product_variant_name: {
          value: variant.label,
          valueConfidence: 100,
        },
        product_quantity: {
          value: quantity,
          valueConfidence: valueConfidence,
          valueConfidenceSource: "manual",
        },
        index: position,
        product_pageNumber: {
          value: parseInt(currentPage, 10),
        },
      };
      setLocalData((prevLocalData) => {
        const updatedProducts = [...prevLocalData.order.products];
        updatedProducts.splice(position, 0, newProduct);
        return {
          ...prevLocalData,
          order: {
            ...prevLocalData.order,
            products: updatedProducts,
          },
        };
      });
      onAddProduct(selectedProductOptions, currentPage, valueConfidence);
    },
    [currentPage, onAddProduct]
  );

  const handleQuickAddProduct = useCallback((event) => {
    event.preventDefault();
  
    const updatedProducts = [...localData.order.products];

    const productIndexes = updatedProducts.map((product, index) => ({
      index: index,
      page: product.product_pageNumber.value,
    }));
    // Find the max product index for the current page or any previous page with products
    let maxIndex = -1;
    for (let page = currentPage; page >= 1; page--) {
      const pageIndexes = productIndexes.filter((p) => p.page === page);
      if (pageIndexes.length > 0) {
        maxIndex = pageIndexes.reduce(
          (max, p) => (p.index > max ? p.index : max),
          maxIndex,
        );
        break;
      }
    }
    const newIndex = maxIndex + 1;
  
    const newProduct = {
      position: newIndex,
      product: {
        value: "",
        label: "",
      },
      quantity: "",
      variant: {
        value: null,
        label: null,
      },
    };

    handleAddProduct(newProduct, true);
  }, [localData.order.products, currentPage, handleAddProduct]);

  const getProductsToValidate = useCallback(() => {
    const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
    const productsToValidate = [];

    items.forEach((product) => {
      const hasVariants = checkProductVariants(product.productID) > 0;
      const isAlreadyValidated =
        product.productStatus >= 75 &&
        product.productQuantityStatus >= 75 &&
        (hasVariants ? product.productVariantStatus >= 75 : true) &&
        (isTemplate2 ? product.productPackingCustomerStatus >= 75 : true);

      const isValidatable =
        product.productID &&
        product.productID !== "" &&
        product.productID !== "None" &&
        checkProductExistsInApi(product.productID) &&
        (hasVariants ? product.productVariant && product.productVariant !== "" : true) &&
        product.productQuantity && product.productQuantity !== "";

      if (!isAlreadyValidated && isValidatable) {
        productsToValidate.push(product);
      }
    });

    return productsToValidate;
  }, [items, checkProductVariants, checkProductExistsInApi, settingsInterface]);

  const handleOpenValidateAllModal = useCallback((event) => {
    event.preventDefault();
    const productsToValidate = getProductsToValidate();
    if (productsToValidate.length === 0) return;
    setProductsToValidateCount(productsToValidate.length);
    onValidateAllModalOpen();
  }, [getProductsToValidate, onValidateAllModalOpen]);

  const handleValidateAllProducts = useCallback(() => {
    const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
    const productsToValidate = getProductsToValidate();

    if (productsToValidate.length === 0) {
      onValidateAllModalClose();
      return;
    }

    // Batch React Hook Form updates
    productsToValidate.forEach((product) => {
      const currentQuantity =
        getValues(`productQuantity-${product.index}`) ?? product.productQuantity;

      let currentPackingCustomer = getValues(`productPackingCustomer-${product.index}`);
      if (isTemplate2 && (!currentPackingCustomer || currentPackingCustomer === "")) {
        currentPackingCustomer = getDefaultPackingCustomer(product.productID);
        if (currentPackingCustomer) {
          setValue(`productPackingCustomer-${product.index}`, currentPackingCustomer);
        }
      }

      setValue(`products-${product.index}.product_id`, product.productID);
      setValue(`products-${product.index}.product_name`, product.productName);
      setValue(`productQuantity-${product.index}`, currentQuantity);

      if (isTemplate2 && currentPackingCustomer) {
        setValue(`products-${product.index}.product_packing_customer`, currentPackingCustomer);
      }
    });

    // Set localData
    setLocalData((prevLocalData) => {
      const updatedProducts = [...prevLocalData.order.products];

      productsToValidate.forEach((product) => {
        const idx = product.index;
        const currentQuantity =
          getValues(`productQuantity-${idx}`) ?? product.productQuantity;

        let currentPackingCustomer = getValues(`productPackingCustomer-${idx}`);
        if (isTemplate2 && (!currentPackingCustomer || currentPackingCustomer === "")) {
          currentPackingCustomer = getDefaultPackingCustomer(product.productID);
        }

        if (updatedProducts[idx]) {
          if (updatedProducts[idx].product_id) {
            updatedProducts[idx].product_id.valueConfidenceSource = "hitl";
          }
          if (updatedProducts[idx].product_quantity) {
            updatedProducts[idx].product_quantity.value = currentQuantity;
            updatedProducts[idx].product_quantity.valueConfidence = "100";
            updatedProducts[idx].product_quantity.valueConfidenceSource = "hitl";
          }
          if (isTemplate2 && updatedProducts[idx].product_packing_customer) {
            if (currentPackingCustomer) {
              updatedProducts[idx].product_packing_customer.value = currentPackingCustomer;
            }
            updatedProducts[idx].product_packing_customer.valueConfidence = 100;
            updatedProducts[idx].product_packing_customer.valueConfidenceSource = "hitl";
          }
        }
      });

      return {
        ...prevLocalData,
        order: { ...prevLocalData.order, products: updatedProducts },
      };
    });

    // update de modifiedValuesbatch
    setModifiedValues((prev) => {
      const newModified = { ...prev };
      productsToValidate.forEach((product) => {
        newModified[product.index] = true;
      });
      return newModified;
    });

    // update trackingError
    productsToValidate.forEach((product) => {
      onSelectedIndicesChange(product.index);
      trackingError(product.index, {
        productError: true,
        quantityError: true,
        variantError: true,
        packingCustomerError: isTemplate2 ? true : undefined,
      });
    });

    onValidateAllModalClose();
  }, [
    setValue,
    getValues,
    setLocalData,
    trackingError,
    settingsInterface,
    getDefaultPackingCustomer,
    onSelectedIndicesChange,
    getProductsToValidate,
    onValidateAllModalClose,
  ]);

  const entityID = localData?.order?.metadata?.entity_id?.value || localData?.process?.entity_id || "";
  const [customerID, setCustomerID] = useState(selectedCustomer?.value || "");

  useEffect(() => {
    // For a customer defined after init
    if (
      selectedCustomer &&
      typeof selectedCustomer === "object" &&
      "value" in selectedCustomer
    ) {
      setCustomerID(selectedCustomer.value || "");
    } else {
      // For a customer defined at init
      setCustomerID(selectedCustomer || "");
    }
  }, [selectedCustomer]);

  const handleQuantityChange = useCallback(
    (index, newValue) => {
      // Update selection status if necessary
      onSelectedIndicesChange(index);

      // Update localData with new product data
      setLocalData((prevLocalData) => {
        const updatedProducts = [...prevLocalData.order.products];
        if (updatedProducts[index] && updatedProducts[index].product_quantity) {
          updatedProducts[index].product_quantity.value = newValue;
          updatedProducts[index].product_quantity.valueConfidence = "100";
          updatedProducts[index].product_quantity.valueConfidenceSource =
            "update";
        }
        return {
          ...prevLocalData,
          order: { ...prevLocalData.order, products: updatedProducts },
        };
      });

      // Sync quantity to parent dataForm
      onProductUpdate?.(index, {
        product_quantity: { value: newValue },
      });

      trackingError(index, { quantityError: true });
    },
    [onSelectedIndicesChange, setLocalData, trackingError, onProductUpdate],
  );

  const handlePackingCustomerChange = useCallback(
    (index, newValue) => {
      onSelectedIndicesChange(index);

      setLocalData((prevLocalData) => {
        const updatedProducts = [...prevLocalData.order.products];
        if (updatedProducts[index]?.product_packing_customer) {
          updatedProducts[index].product_packing_customer.value = newValue;
          updatedProducts[index].product_packing_customer.valueConfidence = 100;
          updatedProducts[index].product_packing_customer.valueConfidenceSource = "update";
        }
        return {
          ...prevLocalData,
          order: { ...prevLocalData.order, products: updatedProducts },
        };
      });

      trackingError(index, { packingCustomerError: true });
    },
    [onSelectedIndicesChange, setLocalData, trackingError],
  );

  const handleDiscountChange = useCallback(
    (index, newValue) => {
      onSelectedIndicesChange(index);

      setLocalData((prevLocalData) => {
        const updatedProducts = [...prevLocalData.order.products];
        if (updatedProducts[index]) {
          if (!updatedProducts[index].product_discount) {
            updatedProducts[index].product_discount = {};
          }
          updatedProducts[index].product_discount.value = newValue;
          updatedProducts[index].product_discount.valueConfidence = 100;
          updatedProducts[index].product_discount.valueConfidenceSource = "update";
        }
        return {
          ...prevLocalData,
          order: { ...prevLocalData.order, products: updatedProducts },
        };
      });

      trackingError(index, { discountError: true });
    },
    [onSelectedIndicesChange, setLocalData, trackingError],
  );

  // Update value isInvalid
  const [modifiedValues, setModifiedValues] = useState({});

  const setHoveredShapeId = (action, canvasId) => {
    onHoveredShapeId(action, canvasId);
  };

  useEffect(() => {
    if (openAddProduct) {
      onModalAddProduct(false);
      onAddProductModalOpen();
    }
  }, [openAddProduct, onModalAddProduct, onAddProductModalOpen]);

  const [internalDebugModalIndex, setInternalDebugModalIndex] = useState({});
  const handleOpenInternalModal = useCallback(
    (index) => {
      setInternalDebugModalIndex(index);
      onInternalDebugModalOpen();
    },
    [onInternalDebugModalOpen],
  );

  const handleCheckboxChange = (isSelected) => {
    setIsProductQuantityMode(isSelected.target.value);
  };
  
  const handleCheckboxToggle = (isSelected) => {
    const firstMode = availableQuantityModes[0]?.key;
    const secondMode = availableQuantityModes[1]?.key;
    
    setIsProductQuantityMode(prevMode => {
      return prevMode === firstMode ? secondMode : firstMode;
    });
  };

  const otherMode = useMemo(() => {
    if (!availableQuantityModes || availableQuantityModes.length < 2) {
      return null;
    }
    
    const firstMode = availableQuantityModes[0]?.key;
    const secondMode = availableQuantityModes[1]?.key;
    
    return isProductQuantityMode === firstMode ? secondMode : firstMode;
  }, [availableQuantityModes, isProductQuantityMode]);

  const renderStatusIcon = (product) => {
    if (localSelectedIndices.has(product.index)) {
      return <GiSandsOfTime className="fill-vistawarningblue text-vistawarningblue text-2xl" />;
    }

    if (checkProductSwitchAvailable(product.productID)) {
      return <RiRepeat2Line className="fill-red-500 text-red-500 text-2xl" />;
    }

    if (!checkProductAvailability(product.productID)) {
      return <CgUnavailable className="fill-red-500 text-red-500 text-2xl" />;
    }

    if (product.productForceVerification) {
      return <PiWarningDiamondFill className="text-2xl" style={{ color: "#b12626" }} />;
    }

    if (product.productConfidenceSource === "notInReferential") {
      return <PiWarningFill className="fill-violet-500 text-violet-500 text-2xl" />;
    }
    if (product.productConfidenceSource === "product_substitution") {
      return <RiRepeat2Line className="fill-violet-500 text-violet-500 text-2xl" />;
    }
    if (product.productConfidenceSource === "product_not_available") {
      return <CgUnavailable className="fill-red-500 text-red-500 text-2xl" />;
    }
  
    const error = currentErrors.find((e) => e.index === product.index);
    if (
      error?.productIdCorrected &&
      error?.quantityIdCorrected &&
      error?.variantIdCorrected &&
      error?.packingCustomerIdCorrected
    ) {
      return <PiCheckSquareFill className="fill-green-500 text-green-500 text-2xl" />;
    }
  
    return <PiWarningFill className="fill-orange-500 text-orange-500 text-2xl" />;
  };

  const calculateTabIndex = (productIndex, columnType) => {
    const baseTabIndex = 100;
    const fieldsPerProduct = isProductVariantVisible ? 4 : 3; // +1 pour le Select dans productQuantity

    const productOffset = productIndex * fieldsPerProduct;

    switch (columnType) {
      case 'productEdit':
        return baseTabIndex + productOffset + 1;
      case 'productVariant':
        return baseTabIndex + productOffset + 2;
      case 'productQuantity':
        return baseTabIndex + productOffset + (isProductVariantVisible ? 3 : 2);
      default:
        return 0;
    }
  };

  const getTabIndexRange = () => {
    const totalProducts = items.length;
    const fieldsPerProduct = isProductVariantVisible ? 4 : 3; // +1 pour le Select dans productQuantity
    const minTabIndex = 101;
    const maxTabIndex = 100 + (totalProducts * fieldsPerProduct);

    return { minTabIndex, maxTabIndex };
  };


  useEffect(() => {
    const handleTabNavigation = (event) => {
      const activeElement = document.activeElement;
      const currentTabIndex = Number.parseInt(activeElement?.getAttribute('tabindex') || '0');
      const { minTabIndex, maxTabIndex } = getTabIndexRange();
      
      if (event.key === 'Tab' && !event.shiftKey) {
        if (currentTabIndex >= minTabIndex && currentTabIndex <= maxTabIndex) {
          event.preventDefault();
          
          const nextTabIndex = currentTabIndex + 1;
          let nextElement = document.querySelector(`[tabindex="${nextTabIndex}"]`);
          
          if (!nextElement) {
            for (let i = nextTabIndex; i <= maxTabIndex; i++) {
              nextElement = document.querySelector(`[tabindex="${i}"]`);
              if (nextElement) {
                console.log('Found element at tabIndex:', i);
                break;
              }
            }
          }
          
          if (!nextElement) {
            nextElement = document.querySelector('[data-cy="order__quickAddProduct"]');
          }
          
          if (nextElement) {
            setTimeout(() => {
              nextElement.focus();
            }, 10);
          }
        } else if (currentTabIndex === 0) {
          const firstProductField = document.querySelector(`[tabindex="${minTabIndex}"]`);
          if (firstProductField && event.target.closest('table')) {
            event.preventDefault();
            setTimeout(() => {
              firstProductField.focus();
            }, 10);
          }
        }
      }
      
    };

    document.addEventListener('keydown', handleTabNavigation, true);
    
    return () => {
      document.removeEventListener('keydown', handleTabNavigation, true);
    };
  }, [items.length, isProductVariantVisible]);


  const renderCell = React.useCallback(
    (product, columnKey, index) => {

      const productID = product.productID || "";
      const productName = product.productName || "";
      const productVariant = product.productVariant || "";
      const productStatus = product.productStatus || 0;
      const productVariantStatus = product.productVariantStatus || 0;
      const productQuantity = product.productQuantity || "";
      const productQuantityStatus = product.productQuantityStatus || 0;
      const productConfidenceSource = product.productConfidenceSource || "";
      const productIdCorrected = product.productIdCorrected || false;
      const variantIdCorrected = product.variantIdCorrected || false;

      const cellValue = product[columnKey];
      //custom style for column
      let cellClass = "";
      if (columnKey === "status") {
        cellClass = "w-[50px] bg-inherit pt-6";
      } else if (columnKey === "productEdit") {
        cellClass = "w-full max-w-0 bg-inherit";
      } else if (columnKey === "productVariant") {
        cellClass = "min-w-[110px] bg-inherit pt-6";
      } else if (columnKey === "productDiscount") {
        cellClass = "min-w-[70px] bg-inherit pt-6";
      } else if (columnKey === "productQuantity") {
        cellClass = "w-[80px] bg-inherit pt-6";
      } else if (columnKey === "actions") {
        cellClass = "w-[70px] bg-inherit pt-6";
      } else {
        cellClass = "max-w-[100px] bg-inherit";
      }
      switch (columnKey) {
        case "status": {
          const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
          const isAllValid =
            product.productIdCorrected &&
            product.quantityIdCorrected &&
            product.variantIdCorrected &&
            (isTemplate2 ? product.packingCustomerIdCorrected : true);

          return (
            <TableCell className={cellClass}>
              <div className="flex items-center justify-center gap-1">
                <Tooltip
                  content={
                    <ProductVerificationTooltip
                      productId={productID}
                      productStatus={productStatus}
                      productQuantityStatus={productQuantityStatus}
                      productVariantStatus={productVariantStatus}
                      productConfidenceSource={productConfidenceSource}
                      hasVariants={checkProductVariants(productID) > 0}
                      productIdCorrected={product.productIdCorrected}
                      quantityIdCorrected={product.quantityIdCorrected}
                      variantIdCorrected={product.variantIdCorrected}
                      packingCustomerIdCorrected={product.packingCustomerIdCorrected}
                      hasPackingCustomer={isTemplate2}
                    />
                  }
                  className={`border-t-4 ${isAllValid ? "border-vistagreen" : "border-orange-500"}`}
                >
                  <div className="flex items-center justify-center">
                    {renderStatusIcon(product)}
                  </div>
                </Tooltip>
              </div>
            </TableCell>
          );
        }
        case "productEdit":
          return (
            <TableCell className={cellClass} tabIndex={-1}>
              <input
                readOnly
                value={`${product.index + 1} / ${productName}`}
                className="text-xs text-vistagrey mb-1 pl-2 truncate w-full bg-transparent border-none outline-none cursor-text"
                tabIndex={-1}
              />
              <SelectComponent
                apiUrl="products"
                endpoint="products"
                customerId={customerID}
                entityId={entityID}
                control={control}
                name={`products-${product.index}`}
                defaultValue={productID}
                valueConfidence={productStatus}
                isModified={productIdCorrected}
                onSelectionChange={(selectedOption) =>
                  handleProductSelectionChange(product.index, selectedOption)
                }
                customTabIndex={calculateTabIndex(product.index, 'productEdit')}
                proposals={product.productProposals}
              />
            </TableCell>
          );
        case "productVariant":
          return (
            <TableCell className={cellClass} tabIndex={-1}>
              {checkProductVariants(productID) > 0 && (
                <SelectComponent
                  apiUrl="products"
                  endpoint="variants"
                  customerId={customerID}
                  productId={productID}
                  control={control}
                  name={`productVariants-${product.index}`}
                  defaultValue={productVariant}
                  valueConfidence={productVariantStatus}
                  isModified={variantIdCorrected}
                  onSelectionChange={(selectedOption) =>
                    handleVariantSelectionChange(product.index, selectedOption)
                  }
                  customTabIndex={calculateTabIndex(product.index, 'productVariant')}
                />
              )}
            </TableCell>
          );
        case "productDiscount":
          return (
            <TableCell className={cellClass} tabIndex={-1}>
              <ProductDiscount
                control={control}
                product={product}
                isDataReadOnlyOperators={isDataReadOnlyOperators}
                getValues={getValues}
                setValue={setValue}
                handleDiscountChange={handleDiscountChange}
                setModifiedValues={setModifiedValues}
                customTabIndex={calculateTabIndex(product.index, 'productDiscount')}
              />
            </TableCell>
          );
        case "productQuantity":
          return (
            <TableCell className={cellClass} tabIndex={-1}>
              <ProductQuantity
                control={control}
                product={product}
                isDataReadOnlyOperators={isDataReadOnlyOperators}
                getValues={getValues}
                setValue={setValue}
                handleQuantityChange={handleQuantityChange}
                handlePackingCustomerChange={handlePackingCustomerChange}
                setModifiedValues={setModifiedValues}
                unit={isProductQuantityMode}
                customTabIndex={calculateTabIndex(product.index, 'productQuantity')}
              />
            </TableCell>
          );
        case "actions": {
          const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
          const isAlreadyValidated =
            productStatus >= 75 &&
            productQuantityStatus >= 75 &&
            (checkProductVariants(productID) ? productVariantStatus >= 75 : true) &&
            (isTemplate2 ? product.productPackingCustomerStatus >= 75 : true);

          return (
            <TableCell className={cellClass}>
              <div className="relative flex items-center justify-end">
                {productID &&
                  productID !== "" &&
                  productID !== "None" &&
                  checkProductExistsInApi(productID) &&
                  (checkProductVariants(productID)
                    ? productVariant && productVariant !== ""
                    : true) &&
                  ((productQuantity &&
                    productQuantity !== "") ||
                    modifiedValues[product.index]) &&
                  !isDataReadOnlyOperators && (
                    <button
                      className={`flex items-center p-2 rounded-full text-white mx-1 ${
                        isAlreadyValidated
                          ? 'bg-vistagrey/10 cursor-not-allowed opacity-50'
                          : 'bg-vistagreen/10 cursor-pointer'
                      }`}
                      data-cy={`order__validateSelection-${product.index}`}
                      tabIndex="-1"
                      onClick={(event) => {
                        event.preventDefault();
                        if (!isAlreadyValidated) {
                          validateSelection(
                            productID,
                            productName,
                            product.index,
                            productQuantity,
                          );
                          setModifiedValues((prev) => ({
                            ...prev,
                            [product.index]: true,
                          }));
                        }
                      }}
                      disabled={isAlreadyValidated}
                    >
                      <IoIosCheckmarkCircle className={isAlreadyValidated ? "fill-vistagrey/50 h-6 w-6" : "fill-vistagreen/70 h-6 w-6"} />
                      <span className="w-0 group-hover/validate:w-auto group-hover/validate:px-2 group-hover/validate:flex  hidden">
                        {t("validate")}
                      </span>
                    </button>
                  )}
                {!isDataReadOnlyOperators && (
                  <Dropdown
                    className={"bg-background border-1 border-default-200"}
                  >
                    <DropdownTrigger>
                      <Button
                        isIconOnly
                        radius="full"
                        size="sm"
                        variant="light"
                        tabIndex="-1"
                        data-cy={`order__secondaryActions-${product.index}`}
                      >
                        <VerticalDotsIcon className="text-default-400" />
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                      <DropdownItem
                        onClick={() => deleteSelection(product.index)}
                        data-cy={`order__deleteActions-${product.index}`}
                      >
                        {t("delete")}
                      </DropdownItem>
                      {isAdminVistaflow && (
                        <DropdownItem
                          onClick={() => handleOpenInternalModal(product.index)}
                        >
                          {t("internal_debug")}
                        </DropdownItem>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
            </TableCell>
          );
        }
        default:
          return (
            <TableCell className={cellClass}>
              <div className="text-bold text-small text-default-500 mt-5">
                {cellValue}
              </div>
            </TableCell>
          );
      }
    },
    [
      control,
      customerID,
      localSelectedIndices,
      modifiedValues,
      deleteSelection,
      getValues,
      handleProductSelectionChange,
      handleQuantityChange,
      handleVariantSelectionChange,
      handleDiscountChange,
      validateSelection,
      checkProductVariants,
      handleOpenInternalModal,
      isAdminVistaflow,
      isDataReadOnlyOperators,
      currentErrors,
      isProductQuantityMode,
      temporaryProduct,
      t,
    ],
  );

  const renderStackedCard = React.useCallback(
    (product) => {
      const productID = product.productID || "";
      const productName = product.productName || "";
      const productVariant = product.productVariant || "";
      const productStatus = product.productStatus || 0;
      const productVariantStatus = product.productVariantStatus || 0;
      const productQuantity = product.productQuantity || "";
      const productQuantityStatus = product.productQuantityStatus || 0;
      const productIdCorrected = product.productIdCorrected || false;
      const variantIdCorrected = product.variantIdCorrected || false;

      const isTemplate2 = settingsInterface?.settings?.display?.product_quantity?.template === 2;
      const isAlreadyValidated =
        productStatus >= 75 &&
        productQuantityStatus >= 75 &&
        (checkProductVariants(productID) ? productVariantStatus >= 75 : true) &&
        (isTemplate2 ? product.productPackingCustomerStatus >= 75 : true);

      const isAllValid =
        product.productIdCorrected &&
        product.quantityIdCorrected &&
        product.variantIdCorrected &&
        (isTemplate2 ? product.packingCustomerIdCorrected : true);

      return (
        <li
          key={`stacked-product-${product.index}`}
          className={`bg-white rounded-lg border border-vistagrey/20 p-3 mb-2 ${
            hoveredShapeDraw.action === "mouseEnter" &&
            hoveredShapeDraw.canvasId === `${product.index}`
              ? "bg-vistagrey/10"
              : ""
          }`}
          onMouseEnter={() => setHoveredShapeId("mouseEnter", product.index)}
          onMouseLeave={() => setHoveredShapeId("mouseLeave", product.index)}
        >
          
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Tooltip
                content={
                  <ProductVerificationTooltip
                    productId={productID}
                    productStatus={productStatus}
                    productQuantityStatus={productQuantityStatus}
                    productVariantStatus={productVariantStatus}
                    productConfidenceSource={product.productConfidenceSource || ""}
                    hasVariants={checkProductVariants(productID) > 0}
                    productIdCorrected={product.productIdCorrected}
                    quantityIdCorrected={product.quantityIdCorrected}
                    variantIdCorrected={product.variantIdCorrected}
                    packingCustomerIdCorrected={product.packingCustomerIdCorrected}
                    hasPackingCustomer={isTemplate2}
                  />
                }
                className={`border-t-4 ${isAllValid ? "border-vistagreen" : "border-orange-500"}`}
              >
                <div className="flex items-center shrink-0">
                  {renderStatusIcon(product)}
                </div>
              </Tooltip>
              <span className="text-xs text-vistagrey font-medium">
                {product.index + 1} / {productName}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {productID &&
                productID !== "" &&
                productID !== "None" &&
                (checkProductVariants(productID)
                  ? productVariant && productVariant !== ""
                  : true) &&
                ((productQuantity && productQuantity !== "") ||
                  modifiedValues[product.index]) &&
                !isDataReadOnlyOperators && (
                  <button
                    className={`flex items-center p-1.5 rounded-full text-white ${
                      isAlreadyValidated
                        ? "bg-vistagrey/10 cursor-not-allowed opacity-50"
                        : "bg-vistagreen/10 cursor-pointer"
                    }`}
                    data-cy={`order__validateSelection-stacked-${product.index}`}
                    tabIndex="-1"
                    onClick={(event) => {
                      event.preventDefault();
                      if (!isAlreadyValidated) {
                        validateSelection(
                          productID,
                          productName,
                          product.index,
                          productQuantity
                        );
                        setModifiedValues((prev) => ({
                          ...prev,
                          [product.index]: true,
                        }));
                      }
                    }}
                    disabled={isAlreadyValidated}
                  >
                    <IoIosCheckmarkCircle
                      className={
                        isAlreadyValidated
                          ? "fill-vistagrey/50 h-5 w-5"
                          : "fill-vistagreen/70 h-5 w-5"
                      }
                    />
                  </button>
                )}
              {!isDataReadOnlyOperators && (
                <Dropdown className="bg-background border-1 border-default-200">
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      radius="full"
                      size="sm"
                      variant="light"
                      tabIndex="-1"
                      data-cy={`order__secondaryActions-stacked-${product.index}`}
                    >
                      <VerticalDotsIcon className="text-default-400" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu>
                    <DropdownItem
                      onPress={() => deleteSelection(product.index)}
                      data-cy={`order__deleteActions-stacked-${product.index}`}
                    >
                      {t("delete")}
                    </DropdownItem>
                    {isAdminVistaflow && (
                      <DropdownItem
                        onPress={() => handleOpenInternalModal(product.index)}
                      >
                        {t("internal_debug")}
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </Dropdown>
              )}
            </div>
          </div>

          {/* Désignation produit */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-vistagrey w-16 shrink-0">{t("article")}:</span>
            <div className="flex-1">
              <SelectComponent
                apiUrl="products"
                endpoint="products"
                customerId={customerID}
                entityId={entityID}
                control={control}
                name={`products-${product.index}`}
                defaultValue={productID}
                valueConfidence={productStatus}
                isModified={productIdCorrected}
                onSelectionChange={(selectedOption) =>
                  handleProductSelectionChange(product.index, selectedOption)
                }
                customTabIndex={calculateTabIndex(product.index, "productEdit")}
                proposals={product.productProposals}
              />
            </div>
          </div>

          {/* Variante */}
          {isProductVariantVisible && checkProductVariants(productID) > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-vistagrey w-16 shrink-0">{t("variant")}:</span>
              <div className="flex-1">
                <SelectComponent
                  apiUrl="products"
                  endpoint="variants"
                  customerId={customerID}
                  productId={productID}
                  control={control}
                  name={`productVariants-${product.index}`}
                  defaultValue={productVariant}
                  valueConfidence={productVariantStatus}
                  isModified={variantIdCorrected}
                  onSelectionChange={(selectedOption) =>
                    handleVariantSelectionChange(product.index, selectedOption)
                  }
                  customTabIndex={calculateTabIndex(product.index, "productVariant")}
                />
              </div>
            </div>
          )}

          {/* Remise */}
          {isProductDiscountVisible && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-vistagrey w-16 shrink-0">{t("discount")}:</span>
              <div className="flex-1">
                <ProductDiscount
                  control={control}
                  product={product}
                  isDataReadOnlyOperators={isDataReadOnlyOperators}
                  getValues={getValues}
                  setValue={setValue}
                  handleDiscountChange={handleDiscountChange}
                  setModifiedValues={setModifiedValues}
                  customTabIndex={calculateTabIndex(product.index, "productDiscount")}
                />
              </div>
            </div>
          )}

          {/* Quantité */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-vistagrey w-16 shrink-0">{t("quantity")}:</span>
            <div className="flex-1">
              <ProductQuantity
                control={control}
                product={product}
                isDataReadOnlyOperators={isDataReadOnlyOperators}
                getValues={getValues}
                setValue={setValue}
                handleQuantityChange={handleQuantityChange}
                handlePackingCustomerChange={handlePackingCustomerChange}
                setModifiedValues={setModifiedValues}
                unit={isProductQuantityMode}
                customTabIndex={calculateTabIndex(product.index, "productQuantity")}
              />
            </div>
          </div>
        </li>
      );
    },
    [
      control,
      customerID,
      modifiedValues,
      deleteSelection,
      getValues,
      setValue,
      handleProductSelectionChange,
      handleQuantityChange,
      handleVariantSelectionChange,
      handlePackingCustomerChange,
      handleDiscountChange,
      validateSelection,
      checkProductVariants,
      handleOpenInternalModal,
      isAdminVistaflow,
      isDataReadOnlyOperators,
      isProductQuantityMode,
      isProductVariantVisible,
      isProductDiscountVisible,
      hoveredShapeDraw,
      settingsInterface,
      t,
    ]
  );

  const classNames = React.useMemo(
    () => ({
      wrapper: ["max-h-[382px]", "max-w-3xl"],
      table: ["border-separate", "border-spacing-y-2"],
      th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
      td: [
        "bg-white",
        "2",
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        "group-data-[middle=true]:before:rounded-none",
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
      ],
    }),
    [],
  );

  // workaround to allow spaces inside a Table row : https://github.com/nextui-org/nextui/issues/1968
  const dummyKeyboardDelegate = Object.fromEntries(
    [
      "getKeyBelow",
      "getKeyAbove",
      "getKeyLeftOf",
      "getKeyRightOf",
      "getKeyPageBelow",
      "getKeyPageAbove",
      "getFirstKey",
      "getLastKey",
    ].map((name) => [name, () => null]),
  );

  return (
    <>
      <div
        className="border-b-1 border-vistagrey/30 px-3 pt-1 pb-3 text-xs font-bold flex justify-between items-center"
        data-cy="order__productsNumber"
      >
        <span>{t("product_list")} ({localData?.order?.products?.length || 0})</span>
        <Tooltip content={viewMode === "table" ? t("stacked_view") : t("table_view")}>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={() => setViewMode(viewMode === "table" ? "stacked" : "table")}
            data-cy="order__toggleViewMode"
          >
            {viewMode === "table" ? (
              <PiCardsFill className="text-lg text-vistagrey" />
            ) : (
              <PiTableFill className="text-lg text-vistagrey" />
            )}
          </Button>
        </Tooltip>
      </div>
      {viewMode === "table" ? (
        <Table
          isCompact
          removeWrapper
          aria-label={t("orders")}
          checkboxesProps={{
            classNames: {
              wrapper:
                "after:bg-foreground after:text-background text-background",
            },
          }}
          classNames={classNames}
          selectedKeys={selectedKeys}
          topContentPlacement="outside"
          onSelectionChange={setSelectedKeys}
          keyboardDelegate={dummyKeyboardDelegate}
        >
          <TableHeader columns={headerColumns}>
            {(column) => (
              <TableColumn key={column.uid} style={column.style}>
                {column.uid === "productQuantity" ? (
                  <div className="text-center">
                    {column.name}
                    {availableQuantityModes.length === 2 && (
                      <div>
                        <CheckboxActive
                          defaultSelected={isProductQuantityMode}
                          onChange={handleCheckboxToggle}
                          textValueOn={isProductQuantityMode}
                          textValueOff={otherMode}
                        />
                      </div>
                    )}

                    {availableQuantityModes.length > 2 && (
                      <div className="h-6">
                        <div className={`absolute ${selectWidth} right-28`}>
                          <Select
                            defaultSelectedKeys={[isProductQuantityMode]}
                            placeholder="Mode"
                            radius="full"
                            size="sm"
                            onChange={handleCheckboxChange}
                            isRequired
                            selectionMode="single"
                            disallowEmptySelection
                            classNames={{
                              trigger: ["bg-vistagreen", "text-white", "h-6", "min-h-6", "data-[hover=true]:bg-vistagreen"]
                            }}
                          >
                            {availableQuantityModes.map((quantity) => (
                              <SelectItem key={quantity.key}>{quantity.label}</SelectItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  column.name
                )}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody emptyContent={t("no_order")} items={items}>
            {items.map((item) => (
              <TableRow
                key={`product-${item.index}`}
                className={
                  hoveredShapeDraw.action === "mouseEnter" &&
                  hoveredShapeDraw.canvasId === `${item.index}`
                    ? "bg-vistagrey/20"
                    : "bg-white"
                }
                onMouseEnter={() => setHoveredShapeId("mouseEnter", item.index)}
                onMouseLeave={() => setHoveredShapeId("mouseLeave", item.index)}
              >
                {(columnKey) => renderCell(item, columnKey)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="px-2 py-2">
          {availableQuantityModes.length >= 2 && (
            <div className="flex items-center justify-end gap-2 mb-3 pb-2 border-b border-vistagrey/20">
              <span className="text-xs text-vistagrey">{t("quantity")}:</span>
              {availableQuantityModes.length === 2 ? (
                <CheckboxActive
                  defaultSelected={isProductQuantityMode}
                  onChange={handleCheckboxToggle}
                  textValueOn={isProductQuantityMode}
                  textValueOff={otherMode}
                />
              ) : (
                <Select
                  defaultSelectedKeys={[isProductQuantityMode]}
                  placeholder="Mode"
                  radius="full"
                  size="sm"
                  onChange={handleCheckboxChange}
                  isRequired
                  selectionMode="single"
                  disallowEmptySelection
                  className={selectWidth}
                  classNames={{
                    trigger: ["bg-vistagreen", "text-white", "h-6", "min-h-6", "data-[hover=true]:bg-vistagreen"]
                  }}
                >
                  {availableQuantityModes.map((quantity) => (
                    <SelectItem key={quantity.key}>{quantity.label}</SelectItem>
                  ))}
                </Select>
              )}
            </div>
          )}
          <ul className="list-none p-0 m-0">
            {items.length === 0 ? (
              <li className="text-center text-vistagrey py-8">{t("no_order")}</li>
            ) : (
              items.map((item) => renderStackedCard(item))
            )}
          </ul>
        </div>
      )}
      <div className="flex justify-between items-center px-2 my-5 pb-3">
        <div className="flex-1" />
        <button
          className="inline-flex items-center border-1 border-vistagreen rounded-lg gap-1 px-2 py-1 text-sm bg-white text-vistagreen"
          onClick={handleQuickAddProduct}
          data-cy="order__quickAddProduct"
        >
          <IoMdAddCircleOutline /> {t("add_product")}
        </button>
        <div className="flex-1 flex justify-end">
          {!isDataReadOnlyOperators && (
            <button
              className="inline-flex items-center border-1 border-vistagreen rounded-lg gap-1 px-2 py-1 text-sm bg-vistagreen text-white"
              onClick={handleOpenValidateAllModal}
              data-cy="order__validateAllProducts"
            >
              <IoIosCheckmarkCircle /> {t("validate_all")}
            </button>
          )}
        </div>
      </div>

      <ValidateAllProductsModal
        isOpen={isValidateAllModalOpen}
        onCancel={onValidateAllModalClose}
        onConfirm={handleValidateAllProducts}
        productsCount={productsToValidateCount}
      />

      <AddProductModal
        isOpen={isAddProductModalOpen}
        onClose={onAddProductModalClose}
        items={items}
        customerID={customerID}
        control={control}
        onAddProduct={(selectedProductOptions) =>
          handleAddProduct(selectedProductOptions)
        }
      />
      <InternalDebugModal
        isOpen={isInternalDebugModalOpen}
        onClose={onInternalDebugModalClose}
        index={internalDebugModalIndex}
      />
    </>
  );
}